import Head from 'next/head';
import React, { useCallback } from 'react';

import { BookingStep } from '@/booking-checkout';
import { CheckoutPageSideEffects } from '@/booking-checkout/components/CheckoutPageSideEffects.component';
import { CheckoutPageSuspense } from '@/booking-checkout/components/CheckoutPageSuspense.component';
import { CurrentBookingContextProvider } from '@/booking/components/CurrentBookingContextProvider.component';
import { HeaderWithWizardContainer } from '@/booking/components/wizard/HeaderWithWizard.container';
import { BookingStructuredDataContainer } from '@/booking/container';
import { BookingAdditionalInfoStepContainer } from '@/booking/container/BookingAdditionalInfoStep.container';
import {
  navigateToBookingOneClickPaymentPage,
  navigateToBookingPaymentPage,
} from '@/booking/helpers/url';
import { usePageTitle } from '@/brand';
import { ExperimentsProvider } from '@/flags/use-experiments';
import Footer from '@/hox-core/components/Footer.component';
import { useTranslation } from '@/i18n';

interface AdditionalInformationProps {
  bookingId: string;
  hotelSlug: string;
}

const AdditionalInformation = ({
  hotelSlug,
  bookingId,
}: AdditionalInformationProps) => {
  const { t } = useTranslation('additionalInformation');

  const pageTitle = usePageTitle(t('title'));

  const handleNextClick = useCallback(
    (hasRequestedOneClickCheckout: boolean) => {
      if (hasRequestedOneClickCheckout) {
        navigateToBookingOneClickPaymentPage({
          hotelSlug,
          bookingId,
        });
        return;
      }
      navigateToBookingPaymentPage({ hotelSlug, bookingId });
    },
    [hotelSlug, bookingId]
  );

  // Booking ID will be set to 'preload' to prefetch all assets for this route
  if (bookingId === 'preload') {
    return null;
  }

  return (
    <CurrentBookingContextProvider
      hotelReferenceId={hotelSlug}
      bookingId={bookingId}
    >
      <ExperimentsProvider context={{ hotelReferenceId: hotelSlug }}>
        <Head>
          <title>{pageTitle}</title>
        </Head>
        <HeaderWithWizardContainer
          bookingId={bookingId}
          hotelSlug={hotelSlug}
          currentPage={BookingStep.AdditionalInfo}
        />
        <CheckoutPageSuspense>
          <CheckoutPageSideEffects
            checkoutStep="additional-info"
            hotelSlug={hotelSlug}
            bookingId={bookingId}
          />
          <BookingStructuredDataContainer
            bookingId={bookingId}
            hotelSlug={hotelSlug}
          />
          <BookingAdditionalInfoStepContainer
            onNextClick={handleNextClick}
            bookingId={bookingId}
            hotelSlug={hotelSlug}
            currentStepNumber={2}
            totalStepCount={3}
          />
        </CheckoutPageSuspense>
        <Footer />
      </ExperimentsProvider>
    </CurrentBookingContextProvider>
  );
};

AdditionalInformation.getInitialProps = ({
  query,
}): AdditionalInformationProps => {
  return { bookingId: query.id, hotelSlug: query.hotelSlug };
};

export default AdditionalInformation;
